"use client";

import React, { useCallback, useState } from "react";
import { Button, Divider, Form, message, Typography } from "antd";
import VerificationCodeInput from "@/app/components/Booking/AppointmentModal/components/VerificationCodeInput";
import { extractErrorMessage } from "@/lib/helpers/error";
import { apiClient } from "@/lib/api/openings-client";
import {
  getCancellationPolicyText,
  getCancelUpfrontNotice,
  isInsideCancellationWindow,
} from "@utils/core";

/** The cancel response's `deposit` outcome (absent from older APIs). */
export type CancelledDeposit = { outcome: string; amountMinor?: number };

interface CancelAppointmentProps {
  appointmentId: string;
  onCanceled?: (deposit?: CancelledDeposit) => void;
  cancellationPolicy?: {
    hours?: number | null;
    note?: string | null;
    lateGraceMinutes?: number | null;
  };
  /** False when the business turned its cancellation window off. */
  cancellationPolicyEnabled?: boolean;
  /** ISO start of the appointment; without it no deposit notice is shown. */
  startsAt?: string | null;
}

const CancelAppointment: React.FC<CancelAppointmentProps> = ({
  appointmentId,
  onCanceled,
  cancellationPolicy,
  cancellationPolicyEnabled = true,
  startsAt,
}) => {
  const [confirm, setConfirm] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState<
    "sms" | "email"
  >("sms");
  const [code, setCode] = useState("");
  // The API refused to resend because a code went out moments ago (the
  // customer closed the dialog, or the tab reloaded). That code is still
  // valid, so the code step is the right place to land — not an error.
  const [codeAlreadySent, setCodeAlreadySent] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  // A disabled window shows no policy copy (booking-flow § 9).
  const policyText = cancellationPolicyEnabled
    ? getCancellationPolicyText(
        cancellationPolicy?.hours,
        cancellationPolicy?.note,
        cancellationPolicy?.lateGraceMinutes,
      )
    : "";
  // booking.cancel.upfrontRefund / upfrontKept: the same window test the
  // cancel applies. Conditional, with no amount: this page must not reveal a
  // payment before the cancel OTP. The commit decides; the result shows it.
  const upfrontNotice = startsAt
    ? getCancelUpfrontNotice({
        insideWindow: isInsideCancellationWindow({
          policyEnabled: cancellationPolicyEnabled,
          policyHours: cancellationPolicy?.hours,
          startsAt,
        }),
        policyHours: cancellationPolicy?.hours,
      })
    : null;

  const onConfirm = useCallback(async () => {
    setIsSendingCode(true);
    try {
      // Overload accepts either string or object; pass string for simplicity
      const res = await apiClient.verifications.sendForCancel(appointmentId);
      if (res.ok && res.data.method) {
        setVerificationMethod(res.data.method);
      }
      setCodeAlreadySent(false);
      setConfirm(true);
    } catch (err: unknown) {
      const apiError = err as { code?: unknown; status?: unknown };
      // Only this dedicated signal guarantees a usable code exists. Other
      // 429s are ordinary request limits and must not open a dead-end form.
      if (
        apiError.status === 429 &&
        apiError.code === "VERIFICATION_CODE_ALREADY_SENT"
      ) {
        setCodeAlreadySent(true);
        setConfirm(true);
        return;
      }
      if (apiError.status === 429) {
        messageApi.error(
          "Too many verification requests. Please wait and try again.",
        );
        return;
      }
      messageApi.error(extractErrorMessage(err));
    } finally {
      setIsSendingCode(false);
    }
  }, [appointmentId, messageApi]);

  const onCancel = useCallback(async () => {
    if (!code) return;
    setIsCanceling(true);
    try {
      const res = await apiClient.appointments.cancelWithCode({
        appointmentId,
        code,
      });
      // Success feedback is rendered by the parent card: this component (and
      // the message holder with it) unmounts as soon as the dialog closes.
      onCanceled?.(res?.data?.deposit);
    } catch (err: unknown) {
      messageApi.error(extractErrorMessage(err));
    } finally {
      setIsCanceling(false);
    }
  }, [appointmentId, code, messageApi, onCanceled]);

  return (
    <>
      {contextHolder}
      {confirm ? (
        <Form layout="vertical" onFinish={onCancel}>
          <Form.Item
            label={
              codeAlreadySent
                ? "We already sent you a verification code. Enter it below."
                : verificationMethod === "email"
                  ? "Verification code sent! Please check your email."
                  : "Verification code sent! Please check your mobile device."
            }
          >
            <VerificationCodeInput
              id="code"
              placeholder="Code"
              value={code}
              onChange={setCode}
              aria-label="verification code"
            />
          </Form.Item>
          <Divider
            style={{
              borderColor: "var(--openings-booking-skin-divider, #f0f0f0)",
            }}
          />
          <Button
            htmlType="submit"
            disabled={!code}
            loading={isCanceling}
            type="primary"
            danger
            aria-busy={isCanceling}
          >
            Cancel Appointment
          </Button>
        </Form>
      ) : (
        <>
          {policyText.split("\n").map((line: string, i: number) =>
            line.trim() ? (
              <Typography.Paragraph key={i} strong={i === 0}>
                {line}
              </Typography.Paragraph>
            ) : null,
          )}

          {upfrontNotice ? (
            <Typography.Paragraph>{upfrontNotice}</Typography.Paragraph>
          ) : null}
          <Typography.Paragraph>
            Do you still wish to cancel your appointment?
          </Typography.Paragraph>
          <Button
            type="primary"
            onClick={onConfirm}
            danger
            loading={isSendingCode}
          >
            Yes, Cancel Appointment
          </Button>
        </>
      )}
    </>
  );
};

export default CancelAppointment;
